import * as ko from 'knockout';

export type Result<T> = {
  data: T;
  error: any;
  isLoading: boolean;
};

/**
 * Creates a Knockout computed observable that maps the result of an async function to a Result
 * object.
 * @param mapFunction The function to map the result of the async function to the data property
 * of the Result object.
 * @param options The options for the observable.
 * @returns The computed observable that is writeable to the observable provided in the options.
 */
export function combineResults<
  TArgs extends ReadonlyArray<KnockoutReadonlyObservable<Result<any>>>,
  TData,
>(
  observables: TArgs,
  combineFunction: (
    ...data: {
      [index in keyof TArgs]: TArgs[index] extends KnockoutReadonlyObservable<Result<infer TValue>>
        ? TValue
        : never;
    }
  ) => TData,
  options: {
    write: (value: Result<TData>) => void;
  }
): KnockoutComputed<Result<TData>> | KnockoutReadonlyComputed<Result<TData>>;
/**
 * Creates a Knockout computed observable that maps the result of an async function to a Result object.
 * @param mapFunction The function to map the result of the async function to the data property of the Result object.
 * @returns The readonly computed observable.
 */
export function combineResults<
  TArgs extends ReadonlyArray<KnockoutReadonlyObservable<Result<any>>>,
  TData,
>(
  observables: TArgs,
  combineFunction: (
    ...data: {
      [index in keyof TArgs]: TArgs[index] extends KnockoutReadonlyObservable<Result<infer TValue>>
        ? TValue
        : never;
    }
  ) => TData
): KnockoutReadonlyComputed<Result<TData>>;
export function combineResults<
  TArgs extends ReadonlyArray<KnockoutReadonlyObservable<Result<any>>>,
  TData,
>(
  observables: TArgs,
  combineFunction: (
    ...data: {
      [index in keyof TArgs]: TArgs[index] extends KnockoutReadonlyObservable<Result<infer TValue>>
        ? TValue
        : never;
    }
  ) => TData,
  options?: { write?: (value: Result<TData>) => void }
): KnockoutComputed<Result<TData>> | KnockoutReadonlyComputed<Result<TData>> {
  if (options?.write) {
    return ko.pureComputed({
      deferEvaluation: true,
      read: () => {
        const results = observables.map((observable) => observable());
        return {
          data: combineFunction(...(results.map((result) => result?.data) as any)),
          isLoading: results.some((result) => result?.isLoading),
          error: results.find((result) => result?.error)?.error ?? null,
        };
      },
      write: options.write,
    }) as KnockoutComputed<Result<TData>>;
  }
  return ko.pureComputed({
    deferEvaluation: true,
    read: () => {
      const results = observables.map((observable) => observable());
      return {
        data: combineFunction(...(results.map((result) => result?.data) as any)),
        isLoading: results.some((result) => result?.isLoading),
        error: results.find((result) => result?.error)?.error ?? null,
      };
    },
  }) as KnockoutReadonlyComputed<Result<TData>>;
}

export function mapResultObservable<TData, TResult>(
  observable: KnockoutReadonlyObservable<Result<TData>>,
  mapFunction: (data: TData) => TResult
): KnockoutReadonlyComputed<Result<TResult>> {
  return ko.pureComputed(() => {
    const { data, error, isLoading } = observable();
    return {
      data: error || isLoading ? (data as unknown as TResult) : mapFunction(data),
      error,
      isLoading,
    };
  });
}

export function observableToAsyncResult<TArg, TData>(
  observable: KnockoutObservable<TArg>,
  mapFunction: (arg: TArg) => Promise<TData>,
  options?: {
    errorValue?: TData;
    initialValue?: TData;
    pendingValue?: TData;
  }
): KnockoutObservable<Result<TData>> & KnockoutSubscription {
  return observable.mapAsync<Result<TData>>(
    (value) =>
      mapFunction(value).then(
        (data) => ({ data, error: null, isLoading: false }),
        (error) => ({ data: options?.errorValue ?? null, error, isLoading: false })
      ),
    {
      initialValue: { data: options?.initialValue, error: null, isLoading: true },
      pendingValue: { data: options?.pendingValue, error: null, isLoading: true },
    }
  );
}

export function reduceResults<TData>(
  reducer: (accumulator: TData, data: TData) => TData,
  ...observables: Array<
    | KnockoutComputed<Result<TData>>
    | KnockoutObservable<Result<TData>>
    | KnockoutReadonlyObservable<Result<TData>>
  >
): KnockoutReadonlyComputed<Result<TData>> {
  return ko.pureComputed(() => ({
    data: observables.map((observable) => observable().data).reduce(reducer),
    error: observables.reduce((err, observable) => err || observable().error, null),
    isLoading: observables.some((observable) => observable().isLoading),
  }));
}
